import { useState, useEffect, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { access_specifier } from "../../config/app.config";
import {
  FiUsers, FiUserPlus, FiLayers, FiGlobe, FiInbox, FiCpu,
  FiArrowRight, FiUser, FiSend, FiZap, FiChevronUp, FiChevronDown,
  FiSearch, FiClock
} from "react-icons/fi";
import "./AdminDashboard.css";

const formatNumber = (num) => {
  if (!num) return "0";
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric"
  });
};

const getPlanLabel = (access) => {
  if (!access) return "Free";
  const codes = access.split(",").map(a => a.trim());
  // Show highest tier
  for (const code of ["5", "9", "62", "61", "6", "4", "3", "2", "1"]) {
    if (codes.includes(code) && access_specifier[code]) {
      return access_specifier[code];
    }
  }
  return "Free";
};

const STAT_CARDS = [
  { key: "totalUsers", label: "Total Users", icon: FiUsers, color: "#7C3AED" },
  { key: "newUsers30d", label: "New (30d)", icon: FiUserPlus, color: "#3B82F6" },
  { key: "totalProjects", label: "Projects", icon: FiLayers, color: "#8B5CF6" },
  { key: "publishedCount", label: "Published", icon: FiGlobe, color: "#10B981" },
  { key: "totalSubmissions", label: "Submissions", icon: FiInbox, color: "#F59E0B" },
  { key: "totalAiTokens", label: "AI Tokens", icon: FiCpu, color: "#EC4899" },
];

const STATUS_COLORS = {
  published: { bg: "#DCFCE7", text: "#166534", bar: "#22C55E" },
  wireframe: { bg: "#F5F3FF", text: "#7C3AED", bar: "#8B5CF6" },
  draft: { bg: "#F3F4F6", text: "#6B7280", bar: "#9CA3AF" },
};

const USER_COLUMNS = [
  { key: "index", label: "#", sortable: false },
  { key: "name", label: "User", sortable: true },
  { key: "access", label: "Plan", sortable: true },
  { key: "active", label: "Status", sortable: true },
  { key: "createdAt", label: "Joined", sortable: true },
  { key: "last_login", label: "Last Login", sortable: true },
  { key: "projectCount", label: "Projects", sortable: true },
  { key: "publishedCount", label: "Published", sortable: true },
  { key: "totalPages", label: "Pages", sortable: true },
  { key: "submissionCount", label: "Submissions", sortable: true },
  { key: "totalCost", label: "Cost", sortable: true },
  { key: "subUserCount", label: "Sub-users", sortable: true },
];

const AdminDashboard = () => {
  const { sendRequest } = useHttp();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const first_name = useSelector((state) => state.auth.first_name);

  // User table state
  const [sortCol, setSortCol] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [loginFilter, setLoginFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    document.title = `${endPoints.title} | Admin Dashboard`;

    sendRequest(
      { url: endPoints.adminDashboard, method: "GET" },
      (response) => {
        if (response.status === 200) {
          setData(response);
        }
        setLoading(false);
      }
    );
  }, [sendRequest]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  const filteredUsers = useMemo(() => {
    if (!data?.allUsers) return [];
    let users = [...data.allUsers];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      users = users.filter(u =>
        (u.first_name || "").toLowerCase().includes(q) ||
        (u.last_name || "").toLowerCase().includes(q) ||
        (u.username || "").toLowerCase().includes(q)
      );
    }

    // Status filter
    if (statusFilter) {
      if (statusFilter === "active") users = users.filter(u => u.active === 1);
      if (statusFilter === "inactive") users = users.filter(u => u.active !== 1);
    }

    // Login recency filter
    if (loginFilter) {
      const now = Date.now();
      users = users.filter(u => {
        if (loginFilter === "never") return !u.last_login;
        if (!u.last_login) return false;
        const diff = now - new Date(u.last_login).getTime();
        if (loginFilter === "24h") return diff <= 86400000;
        if (loginFilter === "7days") return diff <= 604800000;
        if (loginFilter === "30days") return diff <= 2592000000;
        return true;
      });
    }

    // Sort
    users.sort((a, b) => {
      let valA, valB;
      if (sortCol === "name") {
        valA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
        valB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
      } else if (sortCol === "access") {
        valA = getPlanLabel(a.access);
        valB = getPlanLabel(b.access);
      } else if (sortCol === "active") {
        valA = a.active === 1 ? 1 : 0;
        valB = b.active === 1 ? 1 : 0;
      } else if (sortCol === "createdAt" || sortCol === "last_login") {
        valA = a[sortCol] ? new Date(a[sortCol]).getTime() : 0;
        valB = b[sortCol] ? new Date(b[sortCol]).getTime() : 0;
      } else {
        valA = parseFloat(a[sortCol]) || 0;
        valB = parseFloat(b[sortCol]) || 0;
      }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return users;
  }, [data?.allUsers, searchQuery, loginFilter, statusFilter, sortCol, sortDir]);

  if (loading) {
    return (
      <section className="mid_wrap adm-dashboard">
        <div className="container">
          <div className="adm-loading">Loading dashboard...</div>
        </div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="mid_wrap adm-dashboard">
        <div className="container">
          <div className="adm-error">Failed to load dashboard data.</div>
        </div>
      </section>
    );
  }

  const { stats, nicheBreakdown, statusBreakdown, recentSubmissions, recentActivity } = data;
  const maxNicheCount = nicheBreakdown?.[0]?.count || 1;

  return (
    <section className="mid_wrap adm-dashboard">
      <div className="container">
        {/* Header */}
        <div className="adm-header">
          <div>
            <h1 className="adm-title">Admin Dashboard</h1>
            <p className="adm-subtitle">Platform overview and analytics</p>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="adm-stats-grid">
          {STAT_CARDS.map(({ key, label, icon: Icon, color }) => (
            <div className="adm-stat-card" key={key}>
              <div className="adm-stat-icon" style={{ backgroundColor: `${color}12`, color }}>
                <Icon size={20} />
              </div>
              <div className="adm-stat-data">
                <span className="adm-stat-number">{formatNumber(stats[key])}</span>
                <span className="adm-stat-label">{label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Middle Row: Status Breakdown + Top Niches */}
        <div className="adm-row-2col">
          {/* Project Status */}
          <div className="adm-card">
            <h3 className="adm-card-title">Project Status</h3>
            <div className="adm-status-list">
              {statusBreakdown?.map((item) => {
                const cfg = STATUS_COLORS[item.status] || STATUS_COLORS.draft;
                const pct = stats.totalProjects ? Math.round((item.count / stats.totalProjects) * 100) : 0;
                return (
                  <div className="adm-status-row" key={item.status}>
                    <div className="adm-status-info">
                      <span className="adm-status-badge" style={{ backgroundColor: cfg.bg, color: cfg.text }}>
                        {item.status}
                      </span>
                      <span className="adm-status-count">{item.count}</span>
                    </div>
                    <div className="adm-status-bar-track">
                      <div
                        className="adm-status-bar-fill"
                        style={{ width: `${pct}%`, backgroundColor: cfg.bar }}
                      />
                    </div>
                    <span className="adm-status-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Niches */}
          <div className="adm-card">
            <h3 className="adm-card-title">Top Niches</h3>
            <div className="adm-niche-list">
              {nicheBreakdown?.slice(0, 7).map((item, i) => (
                <div className="adm-niche-row" key={item.niche}>
                  <span className="adm-niche-rank">{i + 1}</span>
                  <span className="adm-niche-name">{item.niche}</span>
                  <div className="adm-niche-bar-track">
                    <div
                      className="adm-niche-bar-fill"
                      style={{ width: `${(item.count / maxNicheCount) * 100}%` }}
                    />
                  </div>
                  <span className="adm-niche-count">{item.count}</span>
                </div>
              ))}
              {(!nicheBreakdown || nicheBreakdown.length === 0) && (
                <p className="adm-empty-text">No projects yet</p>
              )}
            </div>
          </div>
        </div>

        {/* User Statistics Table */}
        <div className="adm-card adm-card-full">
          <h3 className="adm-card-title" style={{ marginBottom: 16 }}>
            User Statistics
            <span className="adm-card-title-sub">All users</span>
          </h3>

          {/* Filter Bar */}
          <div className="adm-filter-bar">
            <div className="adm-filter-group">
              <FiSearch size={14} className="adm-filter-icon" />
              <input
                type="text"
                className="adm-filter-search"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="adm-filter-group">
              <FiClock size={14} />
              <select
                className="adm-filter-select"
                value={loginFilter}
                onChange={(e) => setLoginFilter(e.target.value)}
              >
                <option value="">All Users</option>
                <option value="24h">Logged in last 24h</option>
                <option value="7days">Logged in last 7 days</option>
                <option value="30days">Logged in last 30 days</option>
                <option value="never">Never logged in</option>
              </select>
            </div>
            <div className="adm-filter-group">
              <FiUser size={14} />
              <select
                className="adm-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            {(searchQuery || loginFilter || statusFilter) && (
              <button
                className="adm-filter-clear"
                onClick={() => { setSearchQuery(""); setLoginFilter(""); setStatusFilter(""); }}
              >
                Clear
              </button>
            )}
            <span className="adm-filter-count">
              {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="adm-table-wrap">
            <table className="adm-table adm-table-sortable">
              <thead>
                <tr>
                  {USER_COLUMNS.map(col => (
                    <th
                      key={col.key}
                      className={col.sortable ? "adm-th-sortable" : ""}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <span>{col.label}</span>
                      {col.sortable && sortCol === col.key && (
                        <span className="adm-sort-icon">
                          {sortDir === "asc" ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, i) => (
                  <tr key={user.id}>
                    <td className="adm-td-index">{i + 1}</td>
                    <td className="adm-user-cell">
                      <div className="adm-user-avatar">
                        {(user.first_name || "U").charAt(0).toUpperCase()}
                      </div>
                      <div className="adm-user-info">
                        <span
                          className="adm-user-name adm-user-link"
                          onClick={() => history.push(`/admin/user/${user.id}`)}
                        >
                          {user.first_name} {user.last_name}
                        </span>
                        <span className="adm-user-email">{user.username}</span>
                      </div>
                    </td>
                    <td>
                      <span className="adm-plan-badge">{getPlanLabel(user.access)}</span>
                    </td>
                    <td>
                      <span className={`adm-status-pill ${user.active === 1 ? "adm-pill-active" : "adm-pill-inactive"}`}>
                        {user.active === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{user.last_login ? timeAgo(user.last_login) : <span className="adm-text-muted">Never</span>}</td>
                    <td><span className={`adm-stat-badge ${user.projectCount > 0 ? "adm-badge-primary" : "adm-badge-default"}`}>{user.projectCount || 0}</span></td>
                    <td><span className={`adm-stat-badge ${user.publishedCount > 0 ? "adm-badge-success" : "adm-badge-default"}`}>{user.publishedCount || 0}</span></td>
                    <td><span className="adm-stat-badge adm-badge-default">{user.totalPages || 0}</span></td>
                    <td><span className={`adm-stat-badge ${user.submissionCount > 0 ? "adm-badge-primary" : "adm-badge-default"}`}>{user.submissionCount || 0}</span></td>
                    <td><span className={`adm-stat-badge ${user.totalCost > 0 ? "adm-badge-primary" : "adm-badge-default"}`}>${(user.totalCost || 0).toFixed(2)}</span></td>
                    <td><span className={`adm-stat-badge ${user.subUserCount > 0 ? "adm-badge-primary" : "adm-badge-default"}`}>{user.subUserCount || 0}</span></td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={USER_COLUMNS.length} className="adm-empty-text">
                      {searchQuery || loginFilter ? "No users match your filters" : "No users found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="adm-card">
          <h3 className="adm-card-title">Recent Activity</h3>
          <div className="adm-activity-list">
            {recentActivity?.map((item, i) => (
              <div className="adm-activity-item" key={i}>
                <div className={`adm-activity-icon adm-activity-${item.type}`}>
                  {item.type === "user_signup" && <FiUser size={14} />}
                  {item.type === "project_published" && <FiZap size={14} />}
                  {item.type === "form_submission" && <FiSend size={14} />}
                </div>
                <div className="adm-activity-text">
                  {item.type === "user_signup" && (
                    <span><strong>{item.name}</strong> signed up</span>
                  )}
                  {item.type === "project_published" && (
                    <span><strong>{item.name}</strong> was published</span>
                  )}
                  {item.type === "form_submission" && (
                    <span>New submission from <strong>{item.email}</strong> on {item.project}</span>
                  )}
                </div>
                <span className="adm-activity-time">{timeAgo(item.date)}</span>
              </div>
            ))}
            {(!recentActivity || recentActivity.length === 0) && (
              <p className="adm-empty-text">No recent activity</p>
            )}
          </div>
        </div>

        {/* Recent Submissions */}
        <div className="adm-card">
          <div className="adm-card-header-row">
            <h3 className="adm-card-title">Recent Submissions</h3>
            <Link to="/submissions" className="adm-view-all">
              View All <FiArrowRight size={14} />
            </Link>
          </div>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Email</th>
                  <th>Page</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentSubmissions?.map((sub) => (
                  <tr key={sub.id}>
                    <td>{sub.project?.name || "—"}</td>
                    <td>{sub.email || "—"}</td>
                    <td>{sub.page_name || "—"}</td>
                    <td>
                      <span className={`adm-sub-status adm-sub-${sub.status}`}>
                        {sub.status}
                      </span>
                    </td>
                    <td>{timeAgo(sub.createdAt)}</td>
                  </tr>
                ))}
                {(!recentSubmissions || recentSubmissions.length === 0) && (
                  <tr>
                    <td colSpan="5" className="adm-empty-text">No submissions yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminDashboard;
